import { useEffect, useMemo, useState } from 'react'

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputBase,
  Popover,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Bell,
  BellOff,
  Check,
  CheckCheck,
  MoreVertical,
  Search,
  ShieldAlert,
  Thermometer,
  Wifi,
  X as XIcon,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { ROUTES } from '../../constants'
import { useLanguage } from '../../i18n'
import { markAlarmLogRead } from '../../pages/alarms/api'
import useNotificationStream from '../../utils/pusher/hooks/useNotificationStream'
import { commonApis } from '../Api/commonApis'
import { useInboxCounts } from '../hooks/useInboxCounts'
import { normalizeNotification, SEVERITY_META } from '../notifications/normalize'

import { FloatingPanel } from './FloatingPanel'

// FloatingPanel portals to `document.body` with `z-index: 1400`. The
// per-row action Popover must render strictly above it, otherwise the
// 3-dot menu gets visually buried behind the inbox panel.
const ACTION_POPOVER_Z_INDEX = 1500

// ──────────────────────────────────────────────────────────────────────────────
// NotificationDropdown
//
// A feature-rich header popover that replaces the plain bell icon. Two tabs:
//   • Notifications — system messages (calibration reminders, profile updates,
//     invitations, etc.) fetched from GET /notifications
//   • Alarms        — live alarm events fetched from /alarm-profile-log with
//     status=ACTIVE so unresolved alarms surface in the bell badge.
//
// Capabilities:
//   • Per-tab unread counts drive the bell badge and tab pill
//   • Live search across both tabs (filters the currently visible list)
//   • Severity colour-coding + icon avatars for at-a-glance triage
//   • Inline "mark as read" and dismiss affordances
//   • Footer deep-link into the dedicated Notifications / Alarms pages
//   • Theme-aware: every colour is sourced from the CSS variables the
//     theme pipeline publishes, so it renders correctly in both modes
// ──────────────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'notifications', label: 'Notifications' },
  { id: 'alarms', label: 'Alarms' },
]

// SEVERITY_META / severityForNotification / normalizeNotification live in
// src/common/notifications/normalize.js so the full-page Notifications tab
// can reuse the same row→item mapping without duplicating it.

function severityForAlarm(row) {
  const raw = (row?.alarmProfileDetails?.severity || '').toUpperCase()
  const base = SEVERITY_META[raw] || SEVERITY_META.WARNING
  // SeverityAvatar destructures `{ Icon, color, bg }` from `severity` and
  // renders `<Icon size={18} ... />`. Without an Icon here React throws on
  // mount and the whole alarm list fails to render (which is what made
  // the Alarms tab show blank). Pick a severity-specific lucide icon so
  // critical / warning / offline / resolved alarms read distinctly in the
  // inbox the way notifications already do.
  const Icon = {
    CRITICAL: ShieldAlert,
    WARNING: AlertTriangle,
    INFO: Bell,
    OFFLINE: Wifi,
    RESOLVED: Check,
    TEMPERATURE: Thermometer,
  }[raw] || AlertTriangle
  return { ...base, Icon }
}

function relativeTime(value, t) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const diff = Date.now() - date.getTime()
  const minutes = Math.floor(diff / 60_000)
  if (minutes < 1) return t('Just now')
  if (minutes < 60) return `${minutes} ${t('min ago')}`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} ${t('hr ago')}`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days} ${t('d ago')}`
  return date.toLocaleDateString()
}

function normalizeAlarm(row, idx) {
  const sev = severityForAlarm(row)
  const sensorSlug = row?.currentValue?.sensorSlug
  const sensorValue = row?.currentValue?.sensorValue
  const sensorLine = sensorSlug && sensorValue != null ? `${sensorSlug}: ${sensorValue}` : ''
  const profileName = row?.alarmProfileDetails?.alarmProfileName || 'Alarm'
  const deviceName =
    row?.deviceDetails?.serialNumber || row?.deviceDetails?.name || row?.coldRoomName || 'Device'
  const description = sensorLine
    ? `${deviceName} • ${sensorLine}`
    : `${deviceName} • ${row?.alarmProfileDetails?.alarmType || ''}`.trim()
  return {
    id: row?.id ?? row?.uuid ?? `alarm-${idx}`,
    title: profileName,
    description,
    time: row?.createdAt,
    isRead: (row?.status || '').toUpperCase() === 'RESOLVED',
    severity: sev,
    category: row?.alarmProfileDetails?.alarmType || 'ALARM',
    // Direct link to the alarm-log details page. The route
    // `/alarms/log/details/:uuid` is rendered by AlarmLogDetails
    // (the dedicated detail screen with the device, profile, timeline
    // and chart) — landing on the bare /alarms list with a ?logId=
    // would only auto-open the timeline dialog and miss the rest of
    // the detail view. Falls back to the list page (filtered by the
    // numeric id) if the row somehow lacks a uuid.
    href: row?.uuid
      ? `${ROUTES.ALARMS}/log/details/${row.uuid}`
      : `${ROUTES.ALARMS}?logId=${row?.id ?? ''}`,
    raw: row,
  }
}

function SeverityAvatar({ severity, unread }) {
  const { Icon, color, bg } = severity
  return (
    <Box
      sx={{
        position: 'relative',
        width: 38,
        height: 38,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: bg,
        color,
        flexShrink: 0,
      }}
    >
      <Icon size={18} strokeWidth={2.2} />
      {unread && (
        <Box
          sx={{
            position: 'absolute',
            top: -2,
            right: -2,
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: 'var(--primary)',
            border: '2px solid var(--modal-bg-color)',
          }}
        />
      )}
    </Box>
  )
}

function NotificationItem({ item, onMarkRead, onNavigate }) {
  const [hovered, setHovered] = useState(false)
  // Anchor for the per-row action menu. Opening it stops the row's
  // own click handler from also firing navigation.
  const [menuAnchor, setMenuAnchor] = useState(null)
  // Row click is navigate-only. Mark-as-read lives behind the 3-dot
  // popover — clicking the row body must NOT also flip the read flag,
  // otherwise the user can't open a row for inspection without
  // silently dropping it from the unconfirmed list.
  const handleClick = () => {
    onNavigate?.(item)
  }
  const openMenu = (e) => {
    e.stopPropagation()
    setMenuAnchor(e.currentTarget)
  }
  const closeMenu = () => setMenuAnchor(null)
  const handleMarkReadFromMenu = () => {
    closeMenu()
    onMarkRead?.(item)
  }
  return (
    <Box
      role="button"
      tabIndex={0}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      }}
      sx={{
        display: 'flex',
        gap: 1.75,
        alignItems: 'flex-start',
        px: 2,
        py: 1.75,
        cursor: 'pointer',
        position: 'relative',
        borderRadius: '10px',
        mx: 1,
        transition: 'background-color 0.15s ease',
        // Unread items get a subtle tint so they read as a distinct
        // group at a glance without competing with the avatar accent.
        // Hover overrides both so the focus state is unambiguous.
        backgroundColor: item.isRead ? 'transparent' : 'rgba(59,130,246,0.06)',
        '&:hover': {
          backgroundColor: 'var(--menu-hover-bg)',
        },
      }}
    >
      <SeverityAvatar severity={item.severity} unread={!item.isRead} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Top row: category pill (left) + relative time (right).
            Putting the category at the top — rather than the bottom
            like a chip — gives the row a clearer hierarchy: WHAT it
            is, then WHAT happened, then WHERE it's from. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 0.5,
          }}
        >
          <Box
            sx={{
              fontSize: '0.65rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              color: item.severity.color,
              background: item.severity.bg,
              px: 0.85,
              py: 0.2,
              borderRadius: '4px',
              lineHeight: 1.4,
              maxWidth: '70%',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
            title={item.category}
          >
            {item.category}
          </Box>
          <Typography
            sx={{
              fontSize: '0.7rem',
              color: 'var(--text-muted)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {relativeTime(item.time, item._t)}
          </Typography>
        </Box>
        {/* Title — bold for unread, regular for read. Slightly
            larger than before for stronger hierarchy. */}
        <Typography
          sx={{
            fontSize: '0.88rem',
            fontWeight: item.isRead ? 500 : 600,
            color: 'var(--heading-color)',
            lineHeight: 1.35,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            mb: item.description ? 0.5 : 0,
          }}
          title={item.title}
        >
          {item.title}
        </Typography>
        {/* Body / description — only rendered when the row carries
            one. Clamped to 2 lines so a long message never blows up
            the dropdown height; the title tooltip + `title` attribute
            on this Typography carry the full text for hover. */}
        {item.description && (
          <Typography
            sx={{
              fontSize: '0.78rem',
              color: 'var(--text-secondary)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.45,
            }}
            title={item.description}
          >
            {item.description}
          </Typography>
        )}
        {/* Bottom row: optional source label + action menu. The
            action button only surfaces on hover for unread rows,
            matching the previous behaviour — already-read items have
            nothing for the menu to do. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mt: item.description ? 0.85 : 0.5,
            minHeight: 22,
          }}
        >
          {item.source && (
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: 'var(--text-muted)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '65%',
              }}
              title={item.source}
            >
              {item.source}
            </Typography>
          )}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* 3-dot trigger — only meaningful for unread items, since
                the popover only contains "Mark as read". Showing it on
                already-read items would surface an empty action. The
                popover itself is rendered outside this conditional so
                it stays mounted after the cursor leaves the row;
                otherwise the parent box's onMouseLeave would unmount
                it the moment it opens. */}
            {hovered && !item.isRead && (
              <Tooltip title="More actions">
                <IconButton
                  size="small"
                  aria-label="More actions"
                  aria-haspopup="menu"
                  aria-expanded={Boolean(menuAnchor)}
                  onClick={openMenu}
                  sx={{
                    color: 'var(--text-muted)',
                    p: 0.5,
                    '&:hover': { color: 'var(--primary)' },
                  }}
                >
                  <MoreVertical size={14} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {/* Small floating "modal" anchored to the 3-dot trigger.
              Implemented as a Popover and rendered IN-PLACE
              (disablePortal) so it stays inside the FloatingPanel's
              Card DOM tree. Two reasons:
                1. The FloatingPanel portals to `document.body` with
                   z-index 1400; a portaled popover at the default
                   1300 would render behind the panel and the user
                   couldn't see it.
                2. FloatingPanel wraps its content in a ClickAwayListener
                   that closes the panel on any click outside the Card.
                   A portaled popover lives outside the Card and would
                   trigger onClose the moment the user reached for it.
              The popover sits outside the `{hovered && !item.isRead}`
              conditional so it survives the cursor leaving the row on
              its way to the action button. */}
          <Popover
            open={Boolean(menuAnchor)}
            anchorEl={menuAnchor}
            onClose={closeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            disablePortal
            hideBackdrop
            sx={{ zIndex: ACTION_POPOVER_Z_INDEX }}
            PaperProps={{
              onMouseDown: (e) => e.stopPropagation(),
              sx: {
                mt: 0.5,
                minWidth: 180,
                borderRadius: '10px',
                backgroundColor: 'var(--modal-bg-color)',
                border: '1px solid var(--card-border)',
                boxShadow: '0 12px 32px rgba(0,0,0,0.32)',
                overflow: 'visible',
              },
            }}
          >
            <Box role="menu" aria-label="Notification actions" sx={{ py: 0.5 }}>
              <Box
                role="menuitem"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation()
                  handleMarkReadFromMenu()
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    handleMarkReadFromMenu()
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.5,
                  py: 0.85,
                  cursor: 'pointer',
                  color: 'var(--heading-color)',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  transition: 'background-color 0.15s ease',
                  '&:hover, &:focus': {
                    backgroundColor: 'var(--menu-hover-bg)',
                    outline: 'none',
                  },
                }}
              >
                <Check size={14} color="var(--text-secondary)" />
                <span>Mark as read</span>
              </Box>
            </Box>
          </Popover>
        </Box>
      </Box>
    </Box>
  )
}

function EmptyState({ icon: Icon, title, description }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 5,
        px: 3,
        textAlign: 'center',
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(59,130,246,0.10)',
          color: 'var(--primary)',
        }}
      >
        <Icon size={26} strokeWidth={1.8} />
      </Box>
      <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--heading-color)' }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: '0.78rem', color: 'var(--text-muted)', maxWidth: 240 }}>
        {description}
      </Typography>
    </Box>
  )
}

function ItemSkeleton() {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, px: 2, py: 1.5 }}>
      <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: '12px' }} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="60%" height={16} />
        <Skeleton variant="text" width="100%" height={14} />
        <Skeleton variant="text" width="40%" height={12} />
      </Box>
    </Box>
  )
}

export function NotificationDropdown({ open, anchorEl, onClose }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  const [activeTab, setActiveTab] = useState(0)
  const [search, setSearch] = useState('')

  // Default view shows just a couple of items — keeps the popover compact.
  // When the user searches, we drop the cap and surface every match so
  // they can find alarms the default view hides (e.g. resolved alarms).
  const DEFAULT_VISIBLE_COUNT = 3

  // Notifications + alarms feed. Uses the same queryKeys as the
  // header bell badge so both surfaces share cached state — opening the
  // dropdown never triggers a refetch and marking a notification as read
  // instantly clears the badge.
  const { counts, notificationsQuery, alarmsQuery } = useInboxCounts({ enabled: open })

  // Live notifications — subscribes to `notification.created` on the
  // user's private Pusher channel and invalidates every cached
  // notification slice so the bell badge bumps immediately and the
  // dropdown shows the new row on next open. Mounting the binding
  // here (rather than in Header) keeps the subscription co-located
  // with the data it invalidates; the bell badge in Header reads the
  // same query key, so a single invalidate propagates to both.
  useNotificationStream()

  const markReadMutation = useMutation({
    mutationFn: (id) => commonApis.markNotificationRead(id),
    // Optimistic update: flip the row's `isRead` flag in the cached
    // payload immediately so the tab dot and bell badge both drop
    // before the refetch lands. The cached key must match the one
    // useInboxCounts registers (`notifications/inbox`) — without that
    // match the dot would only refresh on the next page navigation.
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['notifications', 'inbox'] })
      const snapshot = queryClient.getQueryData(['notifications', 'inbox'])
      queryClient.setQueryData(['notifications', 'inbox'], (old) => {
        if (!old?.data?.data) return old
        const list = old.data.data.map((row) =>
          row?.id === id
            ? { ...row, isRead: true, read: true, readAt: new Date().toISOString() }
            : row
        )
        return { ...old, data: { ...old.data, data: list } }
      })
      return { snapshot }
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.snapshot) queryClient.setQueryData(['notifications', 'inbox'], ctx.snapshot)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'inbox'] })
    },
  })

  // Alarm mark-as-read: PATCH /alarm-profile-log/:uuid/read. The
  // dropdown's alarms query is keyed by ['alarm-log', 'inbox',
  // 'unconfirmed', …]; on success we optimistically remove the row
  // from the cached slice and decrement `meta.unconfirmed` so the
  // tab pill and bell badge drop on the same frame. The cached key
  // must stay in lockstep with useInboxCounts or the optimistic
  // patch would only land on the next refetch.
  const ALARM_INBOX_KEY = ['alarm-log', 'inbox', 'unconfirmed']
  const markAlarmReadMutation = useMutation({
    mutationFn: (uuid) => markAlarmLogRead(uuid),
    onMutate: async (uuid) => {
      await queryClient.cancelQueries({ queryKey: ALARM_INBOX_KEY })
      const snapshot = queryClient.getQueryData(ALARM_INBOX_KEY)
      queryClient.setQueryData(ALARM_INBOX_KEY, (old) => {
        if (!old?.data?.data) return old
        const next = old.data.data.filter((row) => row?.uuid !== uuid)
        const meta = { ...(old.data.meta || {}) }
        const cur = Number(meta.unconfirmed) || 0
        meta.unconfirmed = Math.max(0, cur - 1)
        meta.confirmed = (Number(meta.confirmed) || 0) + 1
        return { ...old, data: { ...old.data, data: next, meta } }
      })
      return { snapshot }
    },
    onError: (_err, _uuid, ctx) => {
      if (ctx?.snapshot) queryClient.setQueryData(ALARM_INBOX_KEY, ctx.snapshot)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ALARM_INBOX_KEY })
    },
  })

  const notificationRows = useMemo(() => {
    const raw = notificationsQuery.data?.data?.data ?? notificationsQuery.data?.data ?? []
    const list = Array.isArray(raw) ? raw : []
    return list.map((row, idx) => normalizeNotification(row, idx))
  }, [notificationsQuery.data])

  const alarmRows = useMemo(() => {
    const raw = alarmsQuery.data?.data?.data ?? alarmsQuery.data?.data ?? []
    const list = Array.isArray(raw) ? raw : []
    return list.map((row, idx) => normalizeAlarm(row, idx))
  }, [alarmsQuery.data])

  const unreadNotifications = notificationRows.filter((n) => !n.isRead).length
  // Tab pill + footer totals use the app-wide server count, not the
  // page slice (which is already filtered to UNCONFIRMED rows so
  // every visible row is unread by definition).
  const unreadAlarms = counts?.alarmsUnread ?? alarmRows.length
  const totalUnread = unreadNotifications + unreadAlarms

  const currentRows = activeTab === 0 ? notificationRows : alarmRows
  const currentLoading = activeTab === 0 ? notificationsQuery.isLoading : alarmsQuery.isLoading

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase()
    const filtered = currentRows.filter((row) => {
      if (!term) return true
      return (
        row.title?.toLowerCase().includes(term) ||
        row.description?.toLowerCase().includes(term) ||
        row.severity?.label?.toLowerCase().includes(term)
      )
    })
    // Compact default view: only the first N rows, unless the user is
    // actively searching — in which case every match is shown so they can
    // find items the default view hides.
    const capped = term ? filtered : filtered.slice(0, DEFAULT_VISIBLE_COUNT)
    // Attach translator to each row so relativeTime works without re-creating per row
    return capped.map((row) => ({ ...row, _t: t }))
  }, [currentRows, search, t])

  const hiddenCount = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (term) return 0
    const total = currentRows.length
    return Math.max(0, total - visibleRows.length)
  }, [currentRows, visibleRows, search])

  const handleMarkAllRead = () => {
    const unread = currentRows.filter((r) => !r.isRead)
    unread.forEach((row) => {
      // For notifications we have a real endpoint. For alarms we just rely on
      // the row going RESOLVED on the server side, which the next refetch picks up.
      if (activeTab === 0 && row.raw?.uuid) {
        markReadMutation.mutate(row.raw.uuid)
      }
    })
    if (activeTab === 1) {
      // No bulk-read endpoint for alarms; navigate to alarms page where they
      // can be acknowledged. Keep this behaviour safe and obvious.
      onClose?.()
      navigate(ROUTES.ALARMS)
    }
  }

  const handleItemNavigate = (item) => {
    onClose?.()
    // Notifications: route to the standalone Notifications module and
    // open the view modal for the clicked row (via `?open=<uuid>`),
    // rather than jumping straight to the linked resource. The user
    // explicitly chooses "Open resource" inside the modal — gives
    // them a chance to triage before navigating.
    //
    // Alarms: keep the existing behaviour — jump to the alarm-log
    // details page so the user lands on the chart/timeline.
    if (activeTab === 0) {
      const uuid = item.raw?.uuid
      const target = uuid
        ? `${ROUTES.NOTIFICATIONS}?open=${encodeURIComponent(uuid)}`
        : ROUTES.NOTIFICATIONS
      navigate(target)
      return
    }
    if (item.href) navigate(item.href)
  }

  // Reset transient state when the panel closes
  useEffect(() => {
    if (!open) {
      setSearch('')
    }
  }, [open])

  const tabsWithIndicator = [
    { ...TABS[0], count: unreadNotifications },
    { ...TABS[1], count: unreadAlarms },
  ]

  return (
    <FloatingPanel
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      align="right"
      offset={10}
      minWidth={380}
      maxWidth={420}
      width={400}
      sx={{
        backgroundColor: 'var(--modal-bg-color)',
        border: '1px solid var(--card-border)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.32)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          pt: 1.75,
          pb: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, rgba(59,130,246,0.18), rgba(94,125,255,0.18))',
              color: 'var(--primary)',
            }}
          >
            <Bell size={16} strokeWidth={2.3} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: '0.95rem',
                fontWeight: 600,
                color: 'var(--heading-color)',
                lineHeight: 1.2,
              }}
            >
              Inbox
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
              {totalUnread > 0
                ? `${totalUnread} unread ${totalUnread === 1 ? 'item' : 'items'}`
                : "You're all caught up"}
            </Typography>
          </Box>
        </Box>
        {/* <Button
          size="small"
          startIcon={<CheckCheck size={14} />}
          onClick={handleMarkAllRead}
          disabled={currentRows.filter((r) => !r.isRead).length === 0}
          sx={{
            color: 'var(--primary)',
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 500,
            px: 1.25,
            py: 0.5,
            minWidth: 0,
            '&.Mui-disabled': { color: 'var(--text-muted)' },
          }}
        >
          {activeTab === 0 ? 'Mark all read' : 'Acknowledge'}
        </Button> */}
        {activeTab === 0 && (
          <Button
            size="small"
            startIcon={<CheckCheck size={14} />}
            onClick={handleMarkAllRead}
            disabled={currentRows.filter((r) => !r.isRead).length === 0}
            sx={{
              color: 'var(--primary)',
              textTransform: 'none',
              fontSize: '0.75rem',
              fontWeight: 500,
              px: 1.25,
              py: 0.5,
              minWidth: 0,
              '&.Mui-disabled': {
                color: 'var(--text-muted)',
              },
            }}
          >
    Mark all read
          </Button>
        )}
      </Box>

      {/* Tabs */}
      <Box
        sx={{
          px: 1.5,
          borderBottom: '1px solid var(--divider-color)',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 42,
            '& .MuiTab-root': {
              minHeight: 42,
              textTransform: 'none',
              fontSize: '0.8rem',
              fontWeight: 500,
              color: 'var(--text-secondary)',
              '&.Mui-selected': { color: 'var(--primary)' },
            },
            '& .MuiTabs-indicator': {
              height: 2,
              borderRadius: 2,
              backgroundColor: 'var(--primary)',
            },
          }}
        >
          {tabsWithIndicator.map((tab) => (
            <Tab
              key={tab.id}
              label={
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <span>{tab.label}</span>
                  {tab.count > 0 && (
                    // Dynamic unread pill — the numeric count updates
                    // whenever the shared inbox query changes (every
                    // mark-read mutates the cache optimistically, every
                    // refetch re-derives the value), so this number is
                    // always live and never stale. The bell icon at the
                    // top shows the absolute total; this pill breaks it
                    // down per tab.
                    <Box
                      component="span"
                      aria-label={`${tab.count} unread`}
                      sx={{
                        minWidth: 18,
                        height: 18,
                        px: 0.75,
                        borderRadius: '9px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        lineHeight: 1,
                        color: '#ffffff',
                        background: 'var(--danger)',
                        boxShadow: '0 0 0 2px var(--modal-bg-color)',
                        flexShrink: 0,
                      }}
                    >
                      {tab.count > 99 ? '99+' : tab.count}
                    </Box>
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      {/* Search */}
      <Box sx={{ px: 2, py: 1.25 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.25,
            py: 0.5,
            borderRadius: '8px',
            background: 'var(--input-bg)',
            border: '1px solid var(--input-border)',
            transition: 'border-color 0.15s ease',
            '&:focus-within': { borderColor: 'var(--primary)' },
          }}
        >
          <Search size={14} color="var(--text-muted)" />
          <InputBase
            id="notif-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={activeTab === 0 ? 'Search notifications…' : 'Search alarms…'}
            sx={{
              flex: 1,
              fontSize: '0.8rem',
              color: 'var(--input-text)',
              '& input': {
                padding: '4px 0',
                fontSize: '0.8rem',
                '&::placeholder': { color: 'var(--input-placeholder)', opacity: 1 },
              },
            }}
          />
          {search && (
            <IconButton
              size="small"
              onClick={() => setSearch('')}
              sx={{ p: 0.25, color: 'var(--text-muted)' }}
            >
              <XIcon size={12} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* List */}
      <Box
        sx={{
          maxHeight: 380,
          overflowY: 'auto',
          minHeight: 120,
        }}
      >
        {currentLoading ? (
          <Box>
            <ItemSkeleton />
            <Divider sx={{ borderColor: 'var(--divider-color)', mx: 2 }} />
            <ItemSkeleton />
            <Divider sx={{ borderColor: 'var(--divider-color)', mx: 2 }} />
            <ItemSkeleton />
            <Divider sx={{ borderColor: 'var(--divider-color)', mx: 2 }} />
            <ItemSkeleton />
          </Box>
        ) : visibleRows.length === 0 ? (
          <EmptyState
            icon={activeTab === 0 ? BellOff : ShieldAlert}
            title={
              search ? 'No matches' : activeTab === 0 ? 'No notifications' : 'No active alarms'
            }
            description={
              search
                ? `Nothing matches your filter "${search}".`
                : activeTab === 0
                  ? 'New system notifications will appear here.'
                  : 'All alarms have been resolved. Great job!'
            }
          />
        ) : (
          <Box sx={{ py: 0.5 }}>
            {visibleRows.map((item, idx) => (
              <Box key={item.id}>
                <NotificationItem
                  item={item}
                  onMarkRead={(it) => {
                    // Notifications: PUT /notifications/:id/read (existing).
                    // Alarms: PATCH /alarm-profile-log/:uuid/read (new).
                    // The activeTab check guards against a stale render
                    // firing both mutations when the user switches tabs
                    // mid-click.
                    if (activeTab === 0 && it.raw?.uuid) {
                      markReadMutation.mutate(it.raw.uuid)
                    } else if (activeTab === 1 && it.raw?.uuid) {
                      markAlarmReadMutation.mutate(it.raw.uuid)
                    }
                  }}
                  onNavigate={handleItemNavigate}
                />
                {idx < visibleRows.length - 1 && (
                  <Divider sx={{ borderColor: 'var(--divider-color)', mx: 3 }} />
                )}
              </Box>
            ))}
            {hiddenCount > 0 && (
              <Box
                onClick={() => {
                  const target = document.getElementById('notif-search-input')
                  if (target) target.focus()
                }}
                sx={{
                  mx: 2,
                  my: 1,
                  py: 1.25,
                  textAlign: 'center',
                  fontSize: '0.78rem',
                  fontWeight: 500,
                  color: 'var(--primary)',
                  background: 'rgba(59,130,246,0.06)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  '&:hover': { background: 'rgba(59,130,246,0.12)' },
                }}
              >
                +{hiddenCount} more — search to find
              </Box>
            )}
          </Box>
        )}
        {markReadMutation.isPending && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(19, 28, 48, 0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <CircularProgress size={20} thickness={5} sx={{ color: 'var(--primary)' }} />
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Divider sx={{ borderColor: 'var(--divider-color)' }} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          background: 'rgba(255,255,255,0.02)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: totalUnread > 0 ? 'var(--danger)' : 'var(--success)',
              boxShadow: totalUnread > 0 ? '0 0 8px var(--danger)' : '0 0 8px var(--success)',
            }}
          />
          <Typography sx={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
            {totalUnread > 0 ? `${totalUnread} need attention` : 'All clear'}
          </Typography>
        </Box>
        <Button
          size="small"
          endIcon={<span style={{ fontSize: '14px', lineHeight: 1 }}>→</span>}
          onClick={() => {
            onClose?.()
            navigate(activeTab === 0 ? ROUTES.NOTIFICATIONS : ROUTES.ALARM_LOG)
          }}
          sx={{
            color: 'var(--primary)',
            textTransform: 'none',
            fontSize: '0.78rem',
            fontWeight: 600,
            px: 1.25,
            '&:hover': { background: 'rgba(59,130,246,0.08)' },
          }}
        >
          {activeTab === 0 ? t('View All Notifications') : t('View All Alarms')}
        </Button>
      </Box>
    </FloatingPanel>
  )
}

export default NotificationDropdown
